import React, { useEffect, useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'

import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { notify } from 'components/toastify'
import { MESSAGES, STATUS } from 'constants/messages'
import { useSession } from 'helpers/checkSession'
import { BondData, RowBondDataDetail } from 'pages/bond-data-management/interface'
import InfiniteScroll from 'react-infinite-scroll-component'
import ReactTooltip from 'react-tooltip'
import { headerBonDataImportDetail } from './headers'

import IconInformation from 'assets/images/icon-information.svg'
import { BOND_DATA_MANAGEMENT } from 'constants/common'

interface Props {
  data: RowBondDataDetail[]
  fetchMoreBondData: LazyQueryExecFunction<any, OperationVariables>
  fileId: number
}

export interface IDropDownOption {
  value: string
  label: string
}

const includeOrExcludeOptions = [
  {
    value: 'include',
    label: 'Include Columns',
  },
  {
    value: 'exclude',
    label: 'Exclude Columns',
  },
]

const statusFilterOptions = Object.values(BOND_DATA_MANAGEMENT.BOND_ROW_STATUS).map((item) => {
  return {
    value: item,
    label: item,
  }
})

statusFilterOptions.unshift({ value: 'ALL', label: 'ALL' })

const BondDataImportDetailTable: React.FC<Props> = ({ data, fetchMoreBondData, fileId }) => {
  const { timeOutSession } = useSession()
  const [dataTable, setDataTable] = useState<RowBondDataDetail[]>(data)
  const [tableHeaders, setTableHeaders] = useState<string[]>([])
  const [dropdownOptions, setDropdownOptions] = useState<IDropDownOption[]>([])
  const [filterType, setFilterType] = useState<IDropDownOption>()
  const [selectedOptions, setSelectedOptions] = useState<IDropDownOption[]>([])
  const [hasMoreData, setHasMoreData] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>(statusFilterOptions[0].value)

  useEffect(() => {
    if (data && data.length > 0) {
      const headers: string[] = headerBonDataImportDetail.map((item) => item.key)
      setTableHeaders(headers)
      setDropdownOptions(
        headers.map((header) => ({
          value: header,
          label: header,
        })),
      )

      setSelectedOptions([])
      setDataTable(data)
    }
  }, [data])

  const fetchMoreRowData = async (fileId: number, skip: number, take: number, status?: string) => {
    try {
      const variables: { fileId: number; skip: number; take: number; status?: string } = {
        fileId,
        skip,
        take,
      }

      if (status !== 'ALL') {
        variables.status = status
      }

      const response = await fetchMoreBondData({
        variables,
        fetchPolicy: 'no-cache',
      })

      if (response.error) {
        if (response.error.message === MESSAGES.ERROR.E_SESSION_TIME_OUT) {
          notify(MESSAGES.WARNING.W_TIMEOUT_SESSION, STATUS.WARNING)
          timeOutSession()
        } else {
          throw response.error
        }
      }

      const nextBondData: RowBondDataDetail[] = response.data.getBondDataUploadFileByFileId

      return nextBondData
    } catch (error) {
      notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
    }
  }

  const handleFetchNextData = async () => {
    const nextBondData = await fetchMoreRowData(fileId, dataTable.length, 20, statusFilter)
    if (nextBondData) {
      setDataTable(dataTable.concat(nextBondData))
      setHasMoreData(nextBondData.length === 20)
    }
  }

  useEffect(() => {
    fetchMoreRowData(fileId, 0, 20, statusFilter).then((bondData) => {
      if (bondData) {
        setDataTable(bondData)
      }
    })
  }, [])

  const onSelectFilterColumns = (options: MultiValue<IDropDownOption>) => {
    const isInclude = filterType && filterType.value === 'include'
    const filterColumns = options.map((opt) => opt.value)
    const headers: string[] =
      dataTable.length > 0 ? headerBonDataImportDetail.map((item) => item.key) : []
    let updatedHeaders = []
    if (isInclude) {
      updatedHeaders = headers.filter((header) => filterColumns.includes(header))
    } else {
      updatedHeaders = headers.filter((header) => !filterColumns.includes(header))
    }

    const rowIndex = updatedHeaders.findIndex((item) => item === 'rowIndex')
    const statusIndex = updatedHeaders.findIndex((item) => item === 'status')

    if (rowIndex !== -1) {
      updatedHeaders.splice(rowIndex, 1)
      updatedHeaders = ['rowIndex', ...updatedHeaders]
    }

    if (statusIndex !== -1) {
      updatedHeaders.splice(statusIndex, 1)
      updatedHeaders = ['status', ...updatedHeaders]
    }

    setTableHeaders(updatedHeaders)
    setSelectedOptions(options.map((opt) => ({ label: opt.label, value: opt.value })))
  }

  const onHandleChangeTypeFilter = (option: SingleValue<IDropDownOption>) => {
    if (option) {
      setFilterType({
        label: option.label,
        value: option.value,
      })

      setSelectedOptions([])
      setTableHeaders(dataTable.length > 0 ? headerBonDataImportDetail.map((item) => item.key) : [])
    }
  }

  useEffect(() => {
    if (!filterType) return
    fetchMoreRowData(fileId, 0, 20, statusFilter).then((bondData) => {
      if (bondData) {
        setDataTable(bondData)
      }
    })
  }, [filterType])

  const onHandleChangeStatusFilter = (option: SingleValue<IDropDownOption>) => {
    if (option) {
      setStatusFilter(option.value)
    }
  }

  useEffect(() => {
    if (!statusFilter) return

    fetchMoreRowData(fileId, 0, 20, statusFilter).then((bondData) => {
      if (bondData) {
        setDataTable(bondData)
      }
    })
  }, [statusFilter])

  const renderCellData = (item: string | null | number): string | number | null => {
    // if (typeof item === 'string' && !isNaN(parseFloat(item))) {
    //   return parseFloat(item).toLocaleString()
    // } else if (typeof item === 'number' && Number.isInteger(item)) {
    //   return item.toLocaleString()
    // } else {
    //   return item
    // }
    return item
  }

  const renderBodyDataByHeader = (header: string, item: RowBondDataDetail) => {
    const bondData = item.rawJSONData || {}
    let missingRequiredFieldError = ''
    let typeMismatchError = ''

    if (item.errors) {
      const { missingRequiredFields, wrongTypeFields } = item.errors
      if (missingRequiredFields.length > 0 && missingRequiredFields.includes(header)) {
        missingRequiredFieldError = 'Missing required field'
      }

      const typeError = wrongTypeFields.find((item) => item.fieldName === header)
      if (wrongTypeFields.length > 0 && typeError) {
        typeMismatchError += `Type mismatch: Expected: ${typeError.expectedType}, Actual: ${typeError.actualType}`
      }
    }

    const hasError = missingRequiredFieldError !== '' || typeMismatchError != ''

    switch (header) {
      case 'rowIndex':
        return <span>{item.rowIndex}</span>
      case 'status':
        return (
          <span className={item.status === 'FAILED' ? 'text-red-500' : 'text-green-500'}>
            {item.status}
          </span>
        )
      default:
        if (bondData) {
          return (
            <div className="flex">
              <span className={hasError ? 'text-red-500' : ''}>
                {renderCellData(item.rawJSONData[header as keyof BondData])}
              </span>
              {hasError && (
                <a
                  data-tip
                  id={`error-data-tooltip-${header}-${item.rowIndex}`}
                  data-for={`error-data-tooltip-${header}-${item.rowIndex}`}
                  className="pl-1 flex"
                >
                  <img src={IconInformation} alt="Icon-Information" className="w-[18px]" />
                </a>
              )}
              {hasError && (
                <ReactTooltip
                  id={`error-data-tooltip-${header}-${item.rowIndex}`}
                  type="error"
                  effect="solid"
                >
                  <div className="flex flex-col">
                    {missingRequiredFieldError && <span>{missingRequiredFieldError}</span>}
                    {typeMismatchError && <span>{typeMismatchError}</span>}
                  </div>
                </ReactTooltip>
              )}
            </div>
          )
        } else {
          return ''
        }
    }
  }

  return (
    dataTable && (
      <div>
        <div className='flex items-center'>
          <span className='w-[15%]'>Filter rows by status</span>
          <Select
            className="basic-single w-[20%] pr-1"
            onChange={(options) => onHandleChangeStatusFilter(options)}
            classNamePrefix="select"
            placeholder="Status"
            isSearchable={false}
            name="color"
            options={statusFilterOptions}
            defaultValue={statusFilterOptions[0]}
          />
        </div>
        <div className="flex items-center py-3">
          <span className='w-[15%]'>Filter columns</span>
          <Select
            className="basic-single w-[20%] pr-1"
            onChange={(options) => onHandleChangeTypeFilter(options)}
            classNamePrefix="select"
            placeholder="Include/Exclude"
            isSearchable={false}
            name="color"
            options={includeOrExcludeOptions}
            defaultValue={includeOrExcludeOptions[1]}
          />
          <Select
            className="basic-single w-[65%]"
            onChange={(options) => onSelectFilterColumns(options)}
            classNamePrefix="select"
            options={dropdownOptions}
            isClearable={true}
            isSearchable={true}
            name="color"
            closeMenuOnSelect={false}
            isMulti={true}
            value={selectedOptions}
          />
        </div>
        <InfiniteScroll
          dataLength={dataTable.length}
          next={handleFetchNextData}
          hasMore={hasMoreData}
          height={'max-content'}
          style={{ maxHeight: 500, minHeight: 300 }}
          loader={''}
          endMessage={''}
          className="cus-scrollbar"
        >
          <table className="table-auto overflow-scroll border-2 border-neutral-6 rounded-t-lg">
            <thead className="text-body1 text-neutral-2">
              <tr>
                {tableHeaders.map((header, index) => {
                  return (
                    <th
                      className={`bg-primary-shade4 sticky top-0 py-5 px-4`}
                      key={`${header}-${index}`}
                    >
                      <div className="">{header}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            {/* -- Table body --*/}
            <tbody>
              {dataTable.map((bondRow, index) => {
                return (
                  <tr key={`row-${index}`}>
                    {tableHeaders.map((header, hIndex) => {
                      return (
                        <td key={`cell-${hIndex}`} className="px-4 py-2">
                          {renderBodyDataByHeader(header, bondRow)}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    )
  )
}

export default BondDataImportDetailTable
